import React, { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { XsText, TextMd, XlTextSemiBold, XsTextMd } from "../Text";
import { LgLikeIcon } from "Assets/icons";
import { COLORS } from "constants/layout";
import { useIntl } from "react-intl";
import { formatCurrencyWithUnit } from "helpers/string";
import useNavigate from "hooks/useNavigate";

interface PriceDisplayProps {
  hasPrice: boolean;
  price_min: number | null;
  price_max: number | null;
  moq?: number | null;
  moq_unit?: string | null;
  priceStatusConfig: { label?: string };
  farm_id?: string;
  farm_path?: string;
  isMobile: boolean;
}

const PriceDisplay: FC<PriceDisplayProps> = ({
  hasPrice,
  price_min,
  price_max,
  moq,
  moq_unit,
  priceStatusConfig,
  farm_id,
  farm_path,
  isMobile
}) => {
  const intl = useIntl();
  const { history } = useNavigate();

  // Ensure values are numbers, not null
  const minPrice = price_min || 0;
  const maxPrice = price_max || 0;

  const handleAskForPriceClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (farm_path) {
      history.push({
        pathname: farm_path,
        search: "isScroll=true",
      });
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
      {hasPrice ? (
        <XlTextSemiBold className="price">
          {`${formatCurrencyWithUnit(minPrice)}${
            maxPrice && maxPrice > minPrice
              ? ` - ${formatCurrencyWithUnit(maxPrice)}`
              : ""
          }`}
        </XlTextSemiBold>
      ) : (
        <TextMd
          className="price ask-price-link"
          color={COLORS.green[600]}
          onClick={handleAskForPriceClick}
          sx={{
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {intl.formatMessage({ id: "ask_for_price" })}
        </TextMd>
      )}

      {/* Min Order - Keep label and value together */}
      <Box
        sx={{
          display: "inline-flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          component="span"
          color={COLORS.gray[300]}
          sx={{ fontSize: 10 }}
        >
          &bull;
        </Typography>
        <XsText color={COLORS.gray[600]} sx={{ whiteSpace: "nowrap" }}>
          {intl.formatMessage({ id: "min_order" })}:{" "}
          <XsText color={COLORS.gray[900]} component="span">
            {moq
              ? `${moq} ${moq_unit || ""}`
              : intl.formatMessage({ id: "not_specified" })}
          </XsText>
        </XsText>
      </Box>

      {/* Price Status */}
      {priceStatusConfig.label && (
        <XsTextMd className="price-status" sx={{ whiteSpace: "nowrap" }}>
          <Stack direction="row" spacing={0.5} alignItems="center" gap={0.5}>
            <LgLikeIcon />
            {!isMobile && (
              <XsText color={COLORS.red[600]}>
                {intl.formatMessage({
                  id: priceStatusConfig.label,
                })}
              </XsText>
            )}
          </Stack>
        </XsTextMd>
      )}
    </Stack>
  );
};

export default PriceDisplay;
